import React from 'react';
import { FileText, Search, MapPin, Building2, Clock, Shield, CheckCircle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const LegalDocumentServicesGuide = () => {
  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Legal Document Services Guide for California Law Firms 2025",
    "description": "Comprehensive guide to legal document services for California law firms including process serving, document retrieval, court filing, skip tracing, and county recording.",
    "author": {
      "@type": "Organization",
      "name": "Legal Document Specialist"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Legal Document Specialist",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What legal document services do California law firms typically need?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "California law firms typically need process serving for service of process, document retrieval for court records and medical files, court filing and e-filing services, skip tracing for locating parties, county recording for real estate documents, subpoena preparation and service, legal research support, and document management solutions. These services help law firms operate efficiently while maintaining compliance with California legal requirements."
        }
      },
      {
        "@type": "Question",
        "name": "Should law firms outsource legal document services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, outsourcing legal document services offers significant benefits including cost savings (reduces overhead and staff costs), expertise access (specialized knowledge of procedures and compliance), time efficiency (faster turnaround times), risk reduction (professional liability and compliance), scalability (handle volume fluctuations), and technology access (advanced portals and tracking systems). Most California law firms find outsourcing more cost-effective than maintaining in-house capabilities."
        }
      },
      {
        "@type": "Question",
        "name": "How do I choose a legal document service provider?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When choosing a provider, verify licensing and registration (process servers, skip tracers), check insurance and bonding status, review experience in California courts and counties, evaluate technology platforms and client portals, assess turnaround times and rush capabilities, confirm statewide coverage, read client reviews and references, compare pricing and fee structures, and test customer service responsiveness. Look for providers with established relationships with California courts and county offices."
        }
      },
      {
        "@type": "Question",
        "name": "What is the typical cost of legal document services in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Costs vary by service type, urgency, and location. Court filing fees and county recording fees are additional. Volume arrangements are often available for law firms with regular needs. Email your case specifics to quote@legaldocspecialist.com for a quote."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can legal document services be completed?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Service timelines vary: Process serving typically 1-5 days, document retrieval 3-7 days (rush 24-48 hours), court filing same-day to next-day (e-filing faster), skip tracing 24-72 hours, county recording 1-3 days, and subpoena preparation 24-48 hours. Same-day and rush services are available for urgent matters at premium rates. Turnaround times depend on complexity, location, and service provider capacity."
        }
      },
      {
        "@type": "Question",
        "name": "What technology should legal document service providers offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Modern providers should offer secure client portals with 24/7 access, real-time status tracking and updates, electronic document delivery and storage, integration with case management software (Clio, NetDocuments), automated notifications and alerts, digital proof of service, encrypted secure transmission, mobile access capabilities, and detailed reporting and analytics. These technologies improve efficiency and provide better visibility into service status."
        }
      }
    ]
  };

  React.useEffect(() => {
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.textContent = JSON.stringify(guideSchema);
    document.head.appendChild(articleScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(guideSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Legal Document Services Guide for California Law Firms 2025 | Complete Overview"
        description="Comprehensive guide to legal document services for California law firms: process serving, document retrieval, court filing, skip tracing, county recording, and more."
        keywords="legal document services California, law firm services, process serving, document retrieval, court filing, skip tracing, litigation support"
        canonical="/resources/legal-document-services-guide"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'var(--bg-card)',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              marginBottom: '1.5rem',
              border: '1px solid var(--border-medium)'
            }}>
              <Building2 size={16} color="var(--brand-primary)" />
              <span className="caption" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
                Pillar Content Guide
              </span>
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3.5rem)'
            }}>
              Legal Document Services Guide for California Law Firms
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Complete overview of essential legal document services including process serving, document retrieval, court filing, skip tracing, and county recording for California law firms.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/attorney-service" className="btn-primary">
                View All Services
              </Link>
              <a href="/quote" className="btn-secondary">
                Request a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview of Services */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Essential Legal Document Services
            </h2>
            
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', textAlign: 'center', lineHeight: '1.8' }}>
              California law firms rely on specialized legal document services to operate efficiently, maintain compliance, and serve clients effectively. These professional services handle time-consuming administrative tasks, allowing attorneys to focus on legal strategy and client representation.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
              {[
                {
                  icon: <FileText size={28} />,
                  title: 'Process Serving',
                  description: 'Service of legal documents including summons, complaints, subpoenas, and court orders throughout California.',
                  link: '/process-serving',
                  features: ['Registered process servers', 'GPS tracking', 'Proof of service', 'Rush service available']
                },
                {
                  icon: <Search size={28} />,
                  title: 'Document Retrieval',
                  description: 'Retrieval of court records, medical files, business documents, and public records from government agencies.',
                  link: '/document-retrieval',
                  features: ['HIPAA compliant', '24/7 portal access', 'Digital delivery', 'Statewide coverage']
                },
                {
                  icon: <Building2 size={28} />,
                  title: 'Court Filing',
                  description: 'E-filing and traditional court filing services for all California superior courts and appellate courts.',
                  link: '/court-filing',
                  features: ['Same-day filing', 'E-filing certified', 'All 58 counties', 'Status tracking']
                },
                {
                  icon: <MapPin size={28} />,
                  title: 'Skip Tracing',
                  description: 'Professional investigation services to locate parties for service of process and legal proceedings.',
                  link: '/skip-tracing',
                  features: ['Licensed professionals', '85% success rate', '24-72 hour turnaround', 'Legal matters only']
                },
                {
                  icon: <FileText size={28} />,
                  title: 'County Recording',
                  description: 'Recording services for deeds, liens, and legal instruments at all California county recorder offices.',
                  link: '/county-recording',
                  features: ['All 58 counties', 'Notary services', 'Document preparation', 'Fast turnaround']
                },
                {
                  icon: <Clock size={28} />,
                  title: 'Litigation Support',
                  description: 'Comprehensive support including subpoena preparation, document management, and legal research assistance.',
                  link: '/attorney-service',
                  features: ['Subpoena service', 'Document organization', 'Case file management', 'Research support']
                }
              ].map((service, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    color: 'white'
                  }}>
                    {service.icon}
                  </div>
                  
                  <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                    {service.title}
                  </h3>
                  
                  <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    {service.description}
                  </p>
                  
                  <ul style={{ paddingLeft: '0', listStyle: 'none', marginBottom: '1.5rem' }}>
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.85rem',
                        color: 'var(--text-secondary)'
                      }}>
                        <CheckCircle size={14} color="var(--brand-primary)" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to={service.link} style={{
                    color: 'var(--brand-primary)',
                    fontWeight: '600',
                    textDecoration: 'none',
                    fontSize: '0.9rem'
                  }}>
                    Learn More →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of Outsourcing */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Why Law Firms Outsource Legal Document Services
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', textAlign: 'center', lineHeight: '1.8' }}>
              Outsourcing legal document services has become the industry standard for California law firms seeking to improve efficiency, reduce costs, and maintain competitive advantage. The benefits extend far beyond simple cost savings.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {[
                {
                  icon: <TrendingUp size={24} />,
                  title: 'Cost Savings',
                  description: 'Eliminate overhead costs of maintaining in-house staff, equipment, and technology. Pay only for services used without fixed salary and benefit expenses. Most firms save 40-60% compared to in-house operations.'
                },
                {
                  icon: <Shield size={24} />,
                  title: 'Expertise & Compliance',
                  description: 'Access specialized knowledge of California legal procedures, court rules, and compliance requirements. Professional providers maintain current registrations, bonds, insurance, and stay updated on rule changes.'
                },
                {
                  icon: <Clock size={24} />,
                  title: 'Time Efficiency',
                  description: 'Free attorneys and staff from administrative tasks to focus on billable work and client service. Faster turnaround times with dedicated teams and established relationships with courts and agencies.'
                },
                {
                  icon: <Shield size={24} />,
                  title: 'Risk Reduction',
                  description: 'Reduce liability for service errors, missed deadlines, and compliance violations. Professional providers carry insurance and bonds protecting your firm. Proper documentation and proof of service minimize challenges.'
                },
                {
                  icon: <TrendingUp size={24} />,
                  title: 'Scalability',
                  description: 'Easily handle volume fluctuations without hiring or laying off staff. Scale services up during busy periods and down during slow times. No capacity constraints or resource allocation issues.'
                },
                {
                  icon: <FileText size={24} />,
                  title: 'Technology Access',
                  description: 'Leverage advanced client portals, tracking systems, and digital delivery without investment in infrastructure. Real-time status updates, electronic storage, and integration with case management software.'
                }
              ].map((benefit, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    color: 'white'
                  }}>
                    {benefit.icon}
                  </div>
                  
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {benefit.title}
                  </h3>
                  
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Choosing a Provider */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              How to Choose a Legal Document Service Provider
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', textAlign: 'center', lineHeight: '1.8' }}>
              Selecting the right legal document service provider is crucial for your firm's success. Consider these key factors when evaluating potential partners.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {[
                {
                  title: 'Licensing & Registration',
                  description: 'Verify that process servers are registered with county clerks, skip tracers are licensed, and all professionals carry appropriate credentials. Check registration status and expiration dates.',
                  critical: true
                },
                {
                  title: 'Insurance & Bonding',
                  description: 'Confirm the provider carries adequate liability insurance and required bonds ($2,000 for process servers). Request certificates of insurance and verify coverage limits.',
                  critical: true
                },
                {
                  title: 'California Experience',
                  description: 'Look for providers with extensive experience in California courts, counties, and procedures. Local knowledge is essential for navigating court-specific requirements and local rules.',
                  critical: false
                },
                {
                  title: 'Technology Platform',
                  description: 'Evaluate the client portal, tracking capabilities, electronic delivery, and integration options. Modern technology improves efficiency and provides better visibility.',
                  critical: false
                },
                {
                  title: 'Turnaround Times',
                  description: 'Assess standard and rush service turnaround times. Confirm rush capabilities for urgent matters and deadline-driven filings. Review service level agreements.',
                  critical: false
                },
                {
                  title: 'Statewide Coverage',
                  description: 'Ensure the provider covers all 58 California counties or at least all counties where your firm practices. Verify established relationships with local courts and county offices.',
                  critical: false
                },
                {
                  title: 'Client References',
                  description: 'Request references from other California law firms. Review online testimonials, ratings, and complaints. Check Better Business Bureau ratings and professional association memberships.',
                  critical: false
                },
                {
                  title: 'Pricing Structure',
                  description: 'Compare pricing across multiple providers but don\'t choose based solely on cost. Understand fee structures, rush charges, mileage fees, and court/county fees. Ask about volume discounts.',
                  critical: false
                },
                {
                  title: 'Customer Service',
                  description: 'Test responsiveness and communication before committing. Evaluate availability during business hours, response times to inquiries, and problem resolution capabilities.',
                  critical: false
                }
              ].map((factor, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: factor.critical ? '2px solid var(--brand-primary)' : '1px solid var(--border-medium)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <h3 className="heading-4" style={{ margin: 0 }}>
                      {factor.title}
                    </h3>
                    {factor.critical && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: 'var(--brand-primary)',
                        color: 'white',
                        borderRadius: '50px',
                        fontSize: '0.7rem',
                        fontWeight: '600'
                      }}>
                        CRITICAL
                      </span>
                    )}
                  </div>
                  
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {factor.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cost Overview */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Request a Quote for Legal Document Services
            </h2>

            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '3rem', textAlign: 'center', lineHeight: '1.8' }}>
              Every matter is different. Service fees depend on the service type, urgency, complexity, and location, so we quote each request individually.
            </p>

            <div data-testid="guide-quote-request-box" style={{
              backgroundColor: 'var(--bg-card)',
              padding: '3rem',
              borderRadius: '12px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                Email Your Specifics for a Quote
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.8' }}>
                Send the service you need, the documents involved, the service or filing location, and your deadline to{' '}
                <a href="mailto:quote@legaldocspecialist.com" data-testid="guide-quote-email-link" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
                  quote@legaldocspecialist.com
                </a>
                . We'll reply with a quote tailored to your matter.
              </p>
              <Link to="/quote" className="btn-primary" data-testid="guide-quote-page-link" style={{ textDecoration: 'none', display: 'inline-flex', marginBottom: '1.5rem' }}>Start the Quote Questionnaire</Link>
              <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                <strong>Note:</strong> Court filing fees, county recording fees, and mileage charges are additional to service fees. Volume arrangements available for law firms with regular needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'What legal document services do California law firms typically need?',
                answer: 'California law firms typically need process serving for service of process, document retrieval for court records and medical files, court filing and e-filing services, skip tracing for locating parties, county recording for real estate documents, subpoena preparation and service, legal research support, and document management solutions. These services help law firms operate efficiently while maintaining compliance with California legal requirements.'
              },
              {
                question: 'Should law firms outsource legal document services?',
                answer: 'Yes, outsourcing legal document services offers significant benefits including cost savings (reduces overhead and staff costs), expertise access (specialized knowledge of procedures and compliance), time efficiency (faster turnaround times), risk reduction (professional liability and compliance), scalability (handle volume fluctuations), and technology access (advanced portals and tracking systems). Most California law firms find outsourcing more cost-effective than maintaining in-house capabilities.'
              },
              {
                question: 'How do I choose a legal document service provider?',
                answer: 'When choosing a provider, verify licensing and registration (process servers, skip tracers), check insurance and bonding status, review experience in California courts and counties, evaluate technology platforms and client portals, assess turnaround times and rush capabilities, confirm statewide coverage, read client reviews and references, compare pricing and fee structures, and test customer service responsiveness. Look for providers with established relationships with California courts and county offices.'
              },
              {
                question: 'What is the typical cost of legal document services in California?',
                answer: 'Costs vary by service type, urgency, and location. Court filing fees and county recording fees are additional. Volume arrangements are often available for law firms with regular needs. Email your case specifics to quote@legaldocspecialist.com for a quote.'
              },
              {
                question: 'How quickly can legal document services be completed?',
                answer: 'Service timelines vary: Process serving typically 1-5 days, document retrieval 3-7 days (rush 24-48 hours), court filing same-day to next-day (e-filing faster), skip tracing 24-72 hours, county recording 1-3 days, and subpoena preparation 24-48 hours. Same-day and rush services are available for urgent matters at premium rates. Turnaround times depend on complexity, location, and service provider capacity.'
              },
              {
                question: 'What technology should legal document service providers offer?',
                answer: 'Modern providers should offer secure client portals with 24/7 access, real-time status tracking and updates, electronic document delivery and storage, integration with case management software (Clio, NetDocuments), automated notifications and alerts, digital proof of service, encrypted secure transmission, mobile access capabilities, and detailed reporting and analytics. These technologies improve efficiency and provide better visibility into service status.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Ready to Streamline Your Legal Document Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional legal document services for California law firms with statewide coverage
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/attorney-service"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Explore All Services
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalDocumentServicesGuide;
